import "webauthn-polyfills";

/** ブラウザがパスキーを使えるか。http の IP アドレスなど安全でない接続では WebAuthn 自体が無い。 */
export async function passkeySupportedInBrowser(): Promise<boolean> {
	if (!window.isSecureContext || typeof PublicKeyCredential === "undefined") return false;
	const capabilities = await PublicKeyCredential.getClientCapabilities();
	return (
		capabilities.passkeyPlatformAuthenticator === true || capabilities.hybridTransport === true
	);
}

/** サーバーの作成オプション (JSON) でパスキーを作り、検証用に JSON で返す。取り消されたら null。 */
export async function createPasskey(options: PublicKeyCredentialCreationOptionsJSON) {
	const publicKey = PublicKeyCredential.parseCreationOptionsFromJSON(options);
	try {
		const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
		return credential.toJSON();
	} catch (err) {
		return handleCeremonyError(err, "このパスキーはすでに登録されています。");
	}
}

/** メールアドレス欄の自動入力の候補にパスキーを出せるか (conditional mediation)。 */
export async function passkeyAutofillSupported(): Promise<boolean> {
	// 古いブラウザにはこのメソッド自体が無い。
	if (typeof PublicKeyCredential.isConditionalMediationAvailable !== "function") return false;
	return await PublicKeyCredential.isConditionalMediationAvailable();
}

/**
 * サーバーの要求オプション (JSON) でパスキーによる認証を行う。取り消されたら null。
 * mediation に "conditional" を渡すと、ダイアログを出さず自動入力の候補から選ばれるのを待つ。
 */
export async function getPasskey(
	options: PublicKeyCredentialRequestOptionsJSON,
	request?: { mediation?: CredentialMediationRequirement; signal?: AbortSignal },
) {
	const publicKey = PublicKeyCredential.parseRequestOptionsFromJSON(options);
	try {
		const credential = (await navigator.credentials.get({
			publicKey,
			...request,
		})) as PublicKeyCredential;
		return credential.toJSON();
	} catch (err) {
		return handleCeremonyError(err, "このパスキーは使えません。");
	}
}

/** サーバーが受け付けなかったパスキーを、パスワードマネージャーに残さないよう知らせる。 */
export async function signalUnknownPasskey(rpId: string, credentialId: string): Promise<void> {
	if (!PublicKeyCredential.signalUnknownCredential) return;
	await PublicKeyCredential.signalUnknownCredential({ rpId, credentialId });
}

/** サーバーに残っているパスキーだけをパスワードマネージャーに知らせ、削除済みのものを消させる。 */
export async function signalAcceptedPasskeys(
	rpId: string,
	userId: string,
	credentialIds: string[],
): Promise<void> {
	if (!PublicKeyCredential.signalAllAcceptedCredentials) return;
	await PublicKeyCredential.signalAllAcceptedCredentials({
		rpId,
		userId,
		allAcceptedCredentialIds: credentialIds,
	});
}

function handleCeremonyError(err: unknown, invalidStateMessage: string): null {
	if (err instanceof DOMException) {
		// 利用者がダイアログを閉じた・時間切れ・中断は、エラーではなく取り消しとして扱う。
		if (err.name === "NotAllowedError" || err.name === "AbortError") return null;
		if (err.name === "InvalidStateError") throw new Error(invalidStateMessage);
		if (err.name === "SecurityError")
			throw new Error("この接続先ではパスキーを使えません。管理者にお問い合わせください。");
	}
	throw err;
}
